/**
 * Document Preview Modal
 * Belge önizleme modal'ı - PDF ve resimler için
 */
import React, { useState, useEffect } from 'react';
import { X, Download, Edit, Trash2, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';

const DocumentPreviewModal = ({ document, isOpen, onClose, onEdit, onDelete, onDownload }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen || !document) return null;

  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  // File path'i tam URL'ye çevir
  const getFileUrl = () => {
    if (document.fileUrl) return document.fileUrl;
    if (document.file_path) {
      // Local path: /app/backend/uploads/documents/xxx.pdf
      // API URL'e çevir: http://backend/api/uploads/documents/xxx.pdf
      const pathParts = document.file_path.split('/uploads/');
      if (pathParts.length > 1) {
        // Production'da /api prefix gerekiyor (Kubernetes ingress)
        return `${API}/api/uploads/${pathParts[1]}`;
      }
    }
    return null;
  };

  const fileUrl = getFileUrl();
  const fileExtension = document.file_name?.split('.').pop()?.toLowerCase() || 
                       document.file_extension?.replace('.', '').toLowerCase();
  const isPDF = fileExtension === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileExtension);
  const isViewable = isPDF || isImage;

  console.log('📄 Document Preview Debug:', {
    fileName: document.file_name,
    originalPath: document.file_path,
    computedUrl: fileUrl,
    fileExtension,
    isPDF,
    isImage,
    isViewable
  });

  // PDF için Blob URL oluştur (authentication için)
  useEffect(() => {
    if (!isPDF || !document?.id) return;

    const fetchPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        // Use document ID endpoint with authentication
        const pdfUrl = `${API}/api/documents/file/${document.id}`;
        
        const response = await axios.get(pdfUrl, {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (err) {
        console.error('PDF fetch error:', err);
        setError('PDF yüklenemedi: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchPDF();

    // Cleanup: blob URL'yi serbest bırak
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [isPDF, document?.id]);

  // Modal kapanınca blob URL'yi temizle
  useEffect(() => {
    if (!isOpen && blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
  }, [isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(document);
    } else if (fileUrl) {
      // Default download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = document.file_name || 'belge';
      link.click();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold text-gray-900">
              {document.file_name || 'Belge Önizleme'}
            </h2>
            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded uppercase">
              {fileExtension}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(fileUrl, '_blank')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              title="Yeni sekmede aç"
              disabled={!fileUrl}
            >
              <ExternalLink className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownload}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              title="İndir"
              disabled={!fileUrl}
            >
              <Download className="w-5 h-5" />
            </button>
            {onEdit && (
              <button
                onClick={() => onEdit(document)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                title="Düzenle"
              >
                <Edit className="w-5 h-5" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(document)}
                className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md transition-colors"
                title="Sil"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              title="Kapat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Document Info */}
        <div className="px-4 py-3 bg-gray-50 border-b grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600 block mb-1">Belge Tarihi:</span>
            <p className="font-medium text-gray-900">{formatDate(document.document_date)}</p>
          </div>
          <div>
            <span className="text-gray-600 block mb-1">Ülke:</span>
            <p className="font-medium text-gray-900">{document.country_name || '-'}</p>
          </div>
          <div>
            <span className="text-gray-600 block mb-1">Kategori:</span>
            <p className="font-medium text-gray-900">{document.expense_category_name || '-'}</p>
          </div>
          <div>
            <span className="text-gray-600 block mb-1">Tutar:</span>
            <p className="font-medium text-gray-900">
              {document.amount ? `${document.amount.toLocaleString('tr-TR')} ${document.currency_code || ''}` : '-'}
            </p>
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100">
          {!fileUrl ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="bg-white p-8 rounded-lg shadow-sm max-w-md">
                <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Dosya URL'si Bulunamadı
                </h3>
                <p className="text-gray-600 mb-4">
                  Belge dosyasına erişilemiyor.
                </p>
                <pre className="text-xs text-left bg-gray-100 p-2 rounded overflow-auto">
                  {JSON.stringify({
                    file_path: document.file_path,
                    file_name: document.file_name
                  }, null, 2)}
                </pre>
              </div>
            </div>
          ) : isViewable ? (
            <div className="h-full flex items-center justify-center">
              {isPDF && (
                <>
                  {loading ? (
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
                      <p className="text-gray-600">PDF yükleniyor...</p>
                    </div>
                  ) : error ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm max-w-md text-center">
                      <div className="w-16 h-16 bg-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                        <X className="w-8 h-8 text-red-600" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        PDF Yüklenemedi
                      </h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={() => window.open(fileUrl, '_blank')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                      >
                        Yeni Sekmede Aç
                      </button>
                    </div>
                  ) : blobUrl ? (
                    <iframe
                      src={blobUrl}
                      className="w-full h-full border-0 rounded-lg shadow-sm bg-white"
                      title="PDF Önizleme"
                      onError={(e) => {
                        console.error('PDF load error:', e);
                        setError('PDF gösterilemiyor');
                      }}
                    />
                  ) : null}
                </>
              )}
              
              {isImage && (
                <img
                  src={fileUrl}
                  alt={document.file_name}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                  onError={(e) => {
                    console.error('Image load error:', e);
                    console.error('Failed URL:', fileUrl);
                  }}
                />
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="bg-white p-8 rounded-lg shadow-sm max-w-md">
                <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Önizleme Desteklenmiyor
                </h3>
                <p className="text-gray-600 mb-4">
                  Bu dosya türü (.{fileExtension}) için önizleme desteklenmiyor.
                </p>
                <button
                  onClick={() => window.open(fileUrl, '_blank')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  disabled={!fileUrl}
                >
                  Dosyayı Aç
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer with additional info */}
        {document.description && (
          <div className="px-4 py-3 bg-gray-50 border-t">
            <span className="text-xs text-gray-600 block mb-1">Açıklama:</span>
            <p className="text-sm text-gray-900">{document.description}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default DocumentPreviewModal;
